import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGG_OPTIONS, DATE_PARTS, HIGHLIGHT_TYPES, normalizeDatePart } from "@/lib/widgetCatalog";
import { QUICK_CALCS } from "@/lib/quickCalcs";
import { FilterWidgetOptionsEditor } from "@/components/bi/FilterWidgets";
import { Plus, Database, ListFilter, Sigma, Pencil, AlertTriangle } from "lucide-react";
import { SourcePicker } from "@/components/bi/SourcePicker";
import { FederatedEditor } from "@/components/bi/FederatedEditor";
import { WidgetFiltersEditor } from "@/components/bi/WidgetFiltersEditor";
import { GeoFieldsEditor } from "@/components/bi/ChartStyleEditors";
import { BandsEditor } from "@/components/bi/BandsEditor";
import { detectGeoFields } from "@/lib/geo";
import { configFromColumns, normalizeFedSpec } from "@/lib/crossSource";
import { MultiFilterEditor, DataGridEditor } from "@/components/bi/FormatEditors";
import { dimLabelAt } from "@/lib/fieldWells";
import { Section } from "@/components/bi/InspectorBits";
import { InspectorFieldMenu, aggsForType } from "@/components/bi/InspectorFieldMenu";
import { TableColumnsEditor } from "@/components/bi/TableColumnsEditor";
import { ChartGridEditor } from "@/components/bi/ChartGridEditor";
import { SortEngineEditor } from "@/components/bi/InspectorSort";

// Chart types that can render a cross-source (DuckDB combine SQL) result.
const FED_CHART_TYPES = ["bar", "bar_stacked", "line", "area", "combo", "pie", "donut", "scatter",
  "treemap", "radar", "funnel", "tornado", "kpi", "gauge", "table", "pivot", "heatmap", "waterfall"];

// Mapped fields that no longer exist in the source (renamed/deleted column, swapped source).
// Nothing is auto-removed — the author sees exactly what broke and confirms before cleanup.
function InvalidFieldsBanner({ ins }) {
  const { widget, config, schema, patchConfig } = ins;
  const [confirm, setConfirm] = useState(false);
  const names = new Set((schema || []).map((c) => c.name));
  if (!names.size) return null;
  const broken = (kind) => (config[kind === "dim" ? "dimensions" : "measures"] || [])
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => e && e.field && !e.key && !e.band && !e.bin
      && !(e.dataset_id && e.dataset_id !== widget.dataset_id) && !names.has(e.field));
  const badDims = broken("dim");
  const badMeas = broken("meas");
  const total = badDims.length + badMeas.length;
  if (!total) return null;
  const labels = [...badDims, ...badMeas].map(({ e }) => e.field);
  const clean = () => {
    patchConfig({
      dimensions: (config.dimensions || []).filter((_, i) => !badDims.some((b) => b.i === i)),
      measures: (config.measures || []).filter((_, i) => !badMeas.some((b) => b.i === i)),
    });
    setConfirm(false);
  };
  return (
    <>
      <div data-testid="invalid-fields-banner"
        className="rounded-xl border border-[color:rgba(226,75,91,0.35)] bg-[color:rgba(226,75,91,0.07)] p-2.5 space-y-1.5">
        <div className="flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[color:var(--danger)]" />
          <div className="min-w-0">
            <Label className="text-xs text-[color:var(--danger)]">{total} field{total > 1 ? "s" : ""} missing from this source</Label>
            <p className="text-[10px] text-[color:var(--ink-3)] leading-tight break-words">
              <span className="font-mono">{labels.join(", ")}</span> — still mapped, so nothing was silently dropped. Re-point each field or remove them.
            </p>
          </div>
        </div>
        <Button data-testid="invalid-fields-remove" size="sm" variant="outline" className="h-6 text-[10px] px-2 border-[color:rgba(226,75,91,0.4)] text-[color:var(--danger)]"
          onClick={() => setConfirm(true)}>Remove missing fields…</Button>
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-sm" data-testid="invalid-fields-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[color:var(--danger)]" />Remove {total} missing field{total > 1 ? "s" : ""}?</DialogTitle></DialogHeader>
          <p className="text-xs text-[color:var(--ink-3)]">
            <span className="font-mono text-[color:var(--ink-2)]">{labels.join(", ")}</span> will be unmapped from this visual only. Other widgets and the dataset stay untouched, and you can undo this.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(false)} data-testid="invalid-fields-cancel">Keep them</Button>
            <Button data-testid="invalid-fields-confirm" className="bg-[color:var(--danger)] text-white" onClick={clean}>Remove</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// "Assigned Data" section (item 187): governed toggle, chart-specific field wells,
// visual-level filters (with Filtered/Highlighted modes), Top-N, KPI compare.
export function InspectorDataSection({ ins }) {
  const {
    widget, config, opts, datasets, schema, canGovern, governed, setGovernedMode,
    isGeo, isPointMap, isSlicer, isMultiFilter, isDataGrid, isFilterWidget, dims, disp, wells, govDims, govMeas, dimByKey, dimByField,
    measureFields, numericFields, fieldType, addDimension, updateDimension, updateDimensionKey,
    removeDimension, addMeasure, updateMeasure, updateMeasureKey, removeMeasure,
    allowDrop, onDropDim, onDropMeas, patch, patchConfig, patchOpts, hasDataSection,
    sourceDatasets, changeSource,
  } = ins;
  const dsName = (id) => (datasets || []).find((x) => x.id === id)?.name || "other dataset";
  const isTable = widget.type === "table";  // item 24: one ordered Columns well
  const crossEntry = (e) => e && e.dataset_id && e.dataset_id !== widget.dataset_id;
  // Per-visual field rename (display name only affects this widget)
  const [rename, setRename] = useState(null); // {kind: "dim"|"meas", index, value}
  const applyRename = () => {
    if (!rename) return;
    const v = (rename.value || "").trim() || null;
    if (rename.kind === "dim") updateDimension(rename.index, "label", v);
    else updateMeasure(rename.index, "label", v);
    setRename(null);
  };
  const renameBtn = (kind, i, entry, fallback) => (
    <Button size="icon" variant="ghost" title="Rename for this visual"
      data-testid={`${kind}-rename-${i}`}
      className={"h-8 w-8 shrink-0 " + (entry.label ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)]")}
      onClick={() => setRename({ kind, index: i, value: entry.label || fallback || "" })}>
      <Pencil className="w-3.5 h-3.5" />
    </Button>
  );
  return (
    <>
      <Dialog open={!!rename} onOpenChange={(v) => !v && setRename(null)}>
        <DialogContent className="max-w-sm" data-testid="visual-field-rename-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Pencil className="w-4 h-4 text-[color:var(--brand-ocean)]" />Rename for this visual</DialogTitle></DialogHeader>
          <p className="text-xs text-[color:var(--ink-3)]">Only this widget shows the new name — the dataset field and other visuals are untouched. Leave blank to reset.</p>
          <Input data-testid="visual-field-rename-input" autoFocus value={rename?.value || ""}
            onChange={(e) => setRename((s) => ({ ...s, value: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") applyRename(); if (e.key === "Escape") setRename(null); }}
            className="bg-[color:var(--surface)]" placeholder="Display name" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRename(null)}>Cancel</Button>
            <Button data-testid="visual-field-rename-save" className="bg-[color:var(--brand-ocean)] text-white" onClick={applyRename}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
{/* ---------------- Assigned Data ---------------- */}
          {hasDataSection && (
            <Section value="data" icon={Database} title="Assigned Data" testid="inspector-sec-data"
              hint="The fields feeding this visual — every well is specific to the chart type.">
              {widget.dataset_id && ((sourceDatasets || []).length > 1 || (datasets || []).length > 1) && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-[color:var(--ink-3)] shrink-0">Source</Label>
                  <SourcePicker value={widget.dataset_id} onChange={changeSource}
                    datasets={(sourceDatasets || []).some((d) => d.id === widget.dataset_id) ? sourceDatasets
                      : [...(sourceDatasets || []), ...(datasets || []).filter((d) => d.id === widget.dataset_id)]} />
                </div>
              )}
              {widget.type === "perspective" ? (
                <FederatedEditor datasets={datasets} value={normalizeFedSpec(widget.federated_spec)} onChange={(v) => patch({ federated_spec: v })} />
              ) : FED_CHART_TYPES.includes(widget.type) ? (
                <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5 space-y-2">
                  <label className="flex items-center justify-between gap-2 cursor-pointer">
                    <span className="min-w-0">
                      <Label className="text-xs text-[color:var(--ink-2)]">Cross-source SQL</Label>
                      <p className="text-[10px] text-[color:var(--ink-3)] leading-tight">
                        Combine two databases with your own SQL — no relationship needed.
                      </p>
                    </span>
                    <Switch data-testid="inspector-fed-toggle" checked={!!widget.federated_spec}
                      onCheckedChange={(v) => patch({ federated_spec: v ? { subqueries: [], combine_sql: "" } : null })} />
                  </label>
                  {widget.federated_spec && (
                    <>
                      <label className="flex items-center justify-between gap-2 cursor-pointer pt-1 border-t border-[color:var(--glass-border)]">
                        <span className="min-w-0">
                          <Label className="text-xs text-[color:var(--ink-2)]">Respond to dashboard filters</Label>
                          <p className="text-[10px] text-[color:var(--ink-3)] leading-tight">
                            Filters on a source used here narrow that source before the SQL runs.
                          </p>
                        </span>
                        <Switch data-testid="inspector-fed-filters" checked={(widget.federated_spec.filter_mode || "auto") !== "off"}
                          onCheckedChange={(v) => patch({ federated_spec: { ...widget.federated_spec, filter_mode: v ? "auto" : "off" } })} />
                      </label>
                      <FederatedEditor datasets={datasets} value={normalizeFedSpec(widget.federated_spec)}
                        dashboardFilters={ins.dashboardFilters}
                        onChange={(v) => patch({ federated_spec: v })}
                        onMapColumns={(cols, rows) => patchConfig(configFromColumns(cols, rows, widget.type))} />
                    </>
                  )}
                </div>
              ) : null}

              {widget.dataset_id && <InvalidFieldsBanner ins={ins} />}

              {canGovern && (
                <div className="flex items-center justify-between gap-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Database className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-ocean)]" />
                    <div className="min-w-0">
                      <Label className="text-xs text-[color:var(--ink-2)]">Governed model</Label>
                      <p className="text-[10px] text-[color:var(--ink-3)] leading-tight truncate">Business measures &amp; KPIs from the semantic model.</p>
                    </div>
                  </div>
                  <Switch data-testid="inspector-governed-toggle" checked={governed} onCheckedChange={setGovernedMode} className="shrink-0" />
                </div>
              )}

              {isGeo && (
                <div className="text-[11px] text-[color:var(--ink-3)] bg-[color:var(--surface-2)] rounded-lg p-2 space-y-1.5">
                  <p>Use a dimension containing <b>country names</b> (e.g. "United States", "India") — or pick a state map under Visual Options → Map controls and use <b>state names</b>. Values are colored / sized by the measure.</p>
                  <Button data-testid="geo-auto-detect-dim" size="sm" variant="outline" className="h-6 text-[10px] px-2"
                    onClick={() => {
                      const det = detectGeoFields(measureFields);
                      const f = (config.options?.mapShape ? det.state || det.country : det.country || det.state) || det.city;
                      if (f) patchConfig({ dimensions: [{ field: f, date_part: "none" }, ...(config.dimensions || []).slice(1)] });
                    }}>
                    Auto-detect location field
                  </Button>
                </div>
              )}

              {widget.dataset_id && (isSlicer || isFilterWidget) && (
                <div>
                  <Label className="text-xs text-[color:var(--ink-3)]">Filter field</Label>
                  <Select value={config.dimensions?.[0]?.field || ""} onValueChange={(v) => patchConfig({ dimensions: [{ field: v, date_part: "none" }] })}>
                    <SelectTrigger data-testid="slicer-field" className="mt-1.5 w-full bg-[color:var(--surface)]"><SelectValue placeholder="Pick a field" /></SelectTrigger>
                    <SelectContent>{(widget.type === "date_picker" ? dims.filter((c) => fieldType(c.name) === "datetime") : dims).map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
                  </Select>
                  {(isFilterWidget || isSlicer) && (
                    <div className="mt-2 pt-2 border-t border-[color:var(--glass-border)]">
                      <FilterWidgetOptionsEditor widget={widget} opts={config.options || {}} patchOpts={patchOpts} fieldLabel={config.dimensions?.[0]?.field ? disp(config.dimensions[0].field) : ""} />
                    </div>
                  )}
                </div>
              )}

              {widget.dataset_id && isMultiFilter && (
                <MultiFilterEditor config={config} patchConfig={patchConfig} dims={dims} disp={disp} />
              )}

              {widget.dataset_id && isDataGrid && (
                <>
                  <DataGridEditor config={config} opts={opts} patchConfig={patchConfig} schema={schema.filter((c) => !c.hidden)} disp={disp} />
                  <WidgetFiltersEditor config={config} fields={measureFields} disp={disp} patchConfig={patchConfig} datasetId={widget.dataset_id} />
                </>
              )}

              {widget.dataset_id && !isSlicer && !isMultiFilter && !isDataGrid && !isFilterWidget && (
                <>
                  {!(config.dimensions || []).length && !(config.measures || []).length && (
                    <p data-testid="inspector-empty-hint" className="text-[11px] text-[color:var(--ink-3)] bg-[color:rgba(43,78,255,0.06)] border border-[color:rgba(43,78,255,0.2)] rounded-lg p-2">
                      This widget is empty. Add at least one <b>measure</b> (and usually a dimension) to render data.
                    </p>
                  )}
                  {isTable ? (
                    <TableColumnsEditor ins={ins} />
                  ) : isPointMap ? (
                    <GeoFieldsEditor config={config} patchConfig={patchConfig} numericFields={numericFields} allFields={measureFields} disp={disp} />
                  ) : (
                  <div data-testid="well-dimensions" onDragOver={allowDrop} onDrop={onDropDim}>
                    <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-3)]">{isGeo ? "Location (country)" : wells.dimsTitle}</Label><Button data-testid="add-dimension" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={addDimension}><Plus className="w-3.5 h-3.5" /></Button></div>
                    <div className="space-y-2 mt-1.5">
                      {(config.dimensions || []).map((d, i) => (
                        <div key={i} className="flex gap-1.5 items-center">
                          {dimLabelAt(wells, i) && <span className="text-[9px] font-semibold text-[color:var(--brand-ocean)] w-12 shrink-0 truncate" title={dimLabelAt(wells, i)}>{dimLabelAt(wells, i)}</span>}
                          {(d.band || d.bin) ? (
                            <div data-testid={`dim-band-${i}`} title={d.band ? "Measure band (post-aggregation)" : "Numeric bin"}
                              className="flex-1 min-w-0 flex items-center gap-1.5 h-8 px-2 rounded-lg bg-[color:rgba(255,107,53,0.08)] border border-[color:rgba(255,107,53,0.35)]">
                              <span className="text-[9px] font-semibold text-[#FF6B35] shrink-0">{d.band ? "BAND" : "BIN"}</span>
                              <span className="text-xs truncate text-[color:var(--ink-2)]">{d.label || d.alias}</span>
                            </div>
                          ) : crossEntry(d) ? (
                            <div data-testid={`dim-cross-${i}`} title={`Joined from ${dsName(d.dataset_id)} via Model relationships`}
                              className="flex-1 min-w-0 flex items-center gap-1.5 h-8 px-2 rounded-lg bg-[color:rgba(24,169,135,0.08)] border border-[color:rgba(24,169,135,0.35)]">
                              <span className="text-[9px] font-semibold text-[color:var(--brand-mint)] shrink-0 truncate max-w-[76px]">{dsName(d.dataset_id)}</span>
                              <span className="text-xs truncate text-[color:var(--ink-2)]">{d.field}</span>
                            </div>
                          ) : (governed && (d.key || dimByField[d.field])) ? (
                            <Select value={d.key || dimByField[d.field]?.key || ""} onValueChange={(v) => updateDimensionKey(i, v)}>
                              <SelectTrigger data-testid={`dim-key-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue placeholder="Field" /></SelectTrigger>
                              <SelectContent>{govDims.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (
                            <Select value={d.field} onValueChange={(v) => updateDimension(i, "field", v)}>
                              <SelectTrigger data-testid={`dim-field-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {dims.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}
                                {d.field && !dims.some((c) => c.name === d.field) && <SelectItem value={d.field}>{disp(d.field)}</SelectItem>}
                              </SelectContent>
                            </Select>
                          )}
                          {(d.as_type === "date" || (!d.as_type && (governed ? dimByKey[d.key || dimByField[d.field]?.key]?.data_type === "datetime" : fieldType(d.field) === "datetime"))) && (
                            <Select value={normalizeDatePart(d.date_part)} onValueChange={(v) => updateDimension(i, "date_part", v)}>
                              <SelectTrigger data-testid={`dim-datepart-${i}`} className="bg-[color:var(--surface)] w-[104px] shrink-0"><SelectValue /></SelectTrigger>
                              <SelectContent>{DATE_PARTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                          {widget.type === "pivot" && (() => {
                            const hasAxisFlag = (config.dimensions || []).some((x) => x && (x.axis === "col" || x.axis === "row"));
                            const isCol = d.axis === "col" || (!hasAxisFlag && (config.dimensions || []).length >= 2 && i === (config.dimensions || []).length - 1);
                            return (
                              <button data-testid={`dim-axis-${i}`} title="Toggle Row / Column axis" onClick={() => updateDimension(i, "axis", isCol ? "row" : "col")}
                                className={"h-8 px-2 rounded-lg border border-[color:var(--glass-border)] text-[10px] font-semibold shrink-0 transition-colors " + (isCol ? "bg-[color:rgba(43,78,255,0.12)] text-[color:var(--brand-ocean)]" : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)]")}>
                                {isCol ? "Col" : "Row"}
                              </button>
                            );
                          })()}
                          {renameBtn("dim", i, d, dimByKey[d.key]?.label || d.field)}
                          <InspectorFieldMenu kind="dim" index={i} entry={d} config={config} patchConfig={patchConfig} fieldType={fieldType}
                            label={d.label || dimByKey[d.key]?.label || disp(d.field)}
                            onRename={() => setRename({ kind: "dim", index: i, value: d.label || dimByKey[d.key]?.label || d.field || "" })}
                            onRemove={() => removeDimension(i)} />
                        </div>
                      ))}
                    </div>
                  </div>
                  )}

                  {isTable ? null : widget.type === "boxplot" ? (
                    <div>
                      <Label className="text-xs text-[color:var(--ink-3)]">Value field (distribution)</Label>
                      <Select value={config.boxField || ""} onValueChange={(v) => patchConfig({ boxField: v, measures: [{ field: v, agg: "min" }, { field: v, agg: "p25" }, { field: v, agg: "median" }, { field: v, agg: "p75" }, { field: v, agg: "max" }] })}>
                        <SelectTrigger data-testid="boxplot-field" className="mt-1.5 w-full bg-[color:var(--surface)]"><SelectValue placeholder="Pick a numeric field" /></SelectTrigger>
                        <SelectContent>{measureFields.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
                      </Select>
                      <p className="text-[11px] text-[color:var(--ink-3)] mt-1.5">Box shows min &middot; Q1 &middot; median &middot; Q3 &middot; max per group.</p>
                    </div>
                  ) : (
                    <div data-testid="well-measures" onDragOver={allowDrop} onDrop={onDropMeas}>
                      <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-3)]">{wells.measTitle}</Label><Button data-testid="add-measure" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={addMeasure}><Plus className="w-3.5 h-3.5" /></Button></div>
                      {widget.type === "candlestick" && (
                        <div data-testid="candlestick-hint" className={"mt-1.5 text-[11px] rounded-lg p-2 " + ((config.measures || []).length === 4 ? "bg-[color:var(--surface-2)] text-[color:var(--ink-3)]" : "bg-[color:rgba(226,75,91,0.10)] text-[color:var(--danger)]")}>
                          {(config.measures || []).length === 4 ? "Mapped in order \u2192 1) Open  2) Close  3) Low  4) High." : `Candlestick needs exactly 4 measures in order (Open, Close, Low, High). Currently ${(config.measures || []).length}.`}
                        </div>
                      )}
                      <div className="space-y-2 mt-1.5">
                        {(config.measures || []).map((m, i) => (
                          <div key={i} className="space-y-1">
                            <div className="flex gap-1.5 items-center">
                              {widget.type === "candlestick" && i < 4 && <span className="text-[10px] font-semibold text-[color:var(--brand-ocean)] w-10 shrink-0">{["Open", "Close", "Low", "High"][i]}</span>}
                              {crossEntry(m) ? (
                                <div data-testid={`meas-cross-${i}`} title={`Joined from ${dsName(m.dataset_id)} via Model relationships`}
                                  className="flex-1 min-w-0 flex items-center gap-1.5 h-8 px-2 rounded-lg bg-[color:rgba(24,169,135,0.08)] border border-[color:rgba(24,169,135,0.35)]">
                                  <span className="text-[9px] font-semibold text-[color:var(--brand-mint)] shrink-0 truncate max-w-[64px]">{dsName(m.dataset_id)}</span>
                                  <span className="text-xs truncate text-[color:var(--ink-2)] flex-1">{m.field}</span>
                                  <Select value={m.agg || "sum"} onValueChange={(v) => updateMeasure(i, "agg", v)}>
                                    <SelectTrigger data-testid={`meas-cross-agg-${i}`} className="bg-[color:var(--surface)] w-[76px] h-6 text-[10px] shrink-0"><SelectValue /></SelectTrigger>
                                    <SelectContent>{["sum", "avg", "count", "counta", "min", "max"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                                  </Select>
                                </div>
                              ) : (governed && (m.key || govMeas.some((x) => x.field === m.field))) ? (
                                <Select value={m.key || govMeas.find((x) => x.field === m.field && (x.agg || "sum") === (m.agg || "sum"))?.key || govMeas.find((x) => x.field === m.field)?.key || ""} onValueChange={(v) => updateMeasureKey(i, v)}>
                                  <SelectTrigger data-testid={`meas-key-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue placeholder="Measure" /></SelectTrigger>
                                  <SelectContent>{govMeas.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}{c.kind === "calculated" ? "  \u0192x" : ""}</SelectItem>)}</SelectContent>
                                </Select>
                              ) : (
                                <>
                                  <Select value={m.field} onValueChange={(v) => updateMeasure(i, "field", v)}>
                                    <SelectTrigger data-testid={`meas-field-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue /></SelectTrigger>
                                    <SelectContent>{measureFields.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
                                  </Select>
                                  <Select value={m.agg || "sum"} onValueChange={(v) => updateMeasure(i, "agg", v)}>
                                    <SelectTrigger data-testid={`meas-agg-${i}`} className="bg-[color:var(--surface)] w-[100px] shrink-0"><SelectValue /></SelectTrigger>
                                    <SelectContent>{aggsForType(fieldType(m.field), AGG_OPTIONS).map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                                  </Select>
                                </>
                              )}
                              {renameBtn("meas", i, m, m.key ? (govMeas.find((x) => x.key === m.key)?.label || m.key) : m.field)}
                              <InspectorFieldMenu kind="meas" index={i} entry={m} config={config} patchConfig={patchConfig} fieldType={fieldType}
                                label={m.label || (m.key ? (govMeas.find((x) => x.key === m.key)?.label || m.key) : disp(m.field))}
                                onRename={() => setRename({ kind: "meas", index: i, value: m.label || (m.key ? (govMeas.find((x) => x.key === m.key)?.label || m.key) : m.field) || "" })}
                                onRemove={() => removeMeasure(i)} />
                            </div>
                            {m.label && <p className="text-[10px] text-[color:var(--ink-3)] pl-1">Shown as “<span className="text-[color:var(--brand-ocean)]">{m.label}</span>”</p>}
                            <QuickCalcRow measure={m} index={i} update={updateMeasure} disabled={widget.type === "candlestick"} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <WidgetFiltersEditor config={config} fields={measureFields} disp={disp} patchConfig={patchConfig} datasetId={widget.dataset_id} />

                  {["pie", "donut", "semi_donut"].includes(widget.type) && (
                    <ChartGridEditor config={config} patchConfig={patchConfig} dims={dims}
                      measureFields={numericFields.length ? numericFields : measureFields} disp={disp} />
                  )}

                  {!isGeo && !isPointMap && (config.measures || []).length > 0 && (
                    <BandsEditor config={config} patchConfig={patchConfig} numericFields={numericFields} disp={disp} />
                  )}

                  {(config.filters || []).length > 0 && HIGHLIGHT_TYPES.includes(widget.type) && (
                    <div className="flex items-center justify-between gap-2 py-1" data-testid="filter-mode-row">
                      <Label className="text-xs text-[color:var(--ink-3)]">Filter effect</Label>
                      <Select value={config.filterMode || "filtered"} onValueChange={(v) => patchConfig({ filterMode: v === "filtered" ? null : v })}>
                        <SelectTrigger data-testid="filter-mode-select" className="bg-[color:var(--surface)] w-[124px] h-8 text-xs shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="filtered">Filtered</SelectItem>
                          <SelectItem value="highlight">Highlighted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {config.filterMode === "highlight" && (
                    <p className="text-[10px] text-[color:var(--ink-3)] -mt-2">Highlighted keeps every data point visible and dims the ones that don't match the filters.</p>
                  )}

                  <SortEngineEditor config={config} patchConfig={patchConfig} datasetId={widget.dataset_id} />

                  <TopNEditor config={config} patchConfig={patchConfig} />

                  {widget.type === "kpi" && <KpiCompareEditor config={config} opts={opts} schema={schema} patchConfig={patchConfig} patchOpts={patchOpts} />}
                </>
              )}
            </Section>
          )}
    </>
  );
}

// Top N: keep only the N highest/lowest groups by the first measure
function TopNEditor({ config, patchConfig }) {
  const tn = config.topN || null;
  const hasMeasure = (config.measures || []).length > 0;
  if (!hasMeasure && !tn) return null;
  return (
    <div data-testid="topn-editor" className="pt-1 border-t border-[color:var(--glass-border)]">
      <div className="flex items-center justify-between gap-2 py-1">
        <Label className="text-sm text-[color:var(--ink-2)] flex items-center gap-1"><ListFilter className="w-3.5 h-3.5" />Top N (by first measure)</Label>
        <Switch data-testid="topn-toggle" checked={!!tn} onCheckedChange={(v) => patchConfig({ topN: v ? { n: 10, direction: "desc" } : null })} className="shrink-0" />
      </div>
      {tn && (
        <div className="flex gap-1.5 mt-1">
          <Input data-testid="topn-n" type="number" min={1} max={1000} value={tn.n} onChange={(e) => patchConfig({ topN: { ...tn, n: Number(e.target.value) || 1 } })} className="bg-[color:var(--surface)] w-[90px] h-8 text-xs" />
          <Select value={tn.direction || "desc"} onValueChange={(v) => patchConfig({ topN: { ...tn, direction: v } })}>
            <SelectTrigger data-testid="topn-direction" className="bg-[color:var(--surface)] flex-1 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Top (highest)</SelectItem>
              <SelectItem value="asc">Bottom (lowest)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

// KPI period-over-period comparison editor
function KpiCompareEditor({ config, opts, schema, patchConfig, patchOpts }) {
  const dateFields = schema.filter((c) => c.type === "datetime").map((c) => c.name);
  const compare = opts.compare || null;
  const setCompare = (field, period) => {
    if (!field) {
      const { compare: _c, ...rest } = opts;
      patchConfig({ dimensions: [], options: rest });
      return;
    }
    patchConfig({ dimensions: [{ field, date_part: period }], options: { ...opts, compare: { field, period } } });
  };
  return (
    <div className="pt-1 border-t border-[color:var(--glass-border)]">
      <Label className="text-xs text-[color:var(--ink-3)]">Compare over time (vs previous period)</Label>
      <div className="flex gap-1.5 mt-1.5">
        <Select value={compare?.field || "__off__"} onValueChange={(v) => setCompare(v === "__off__" ? null : v, compare?.period || "month")}>
          <SelectTrigger data-testid="kpi-compare-field" className="bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue placeholder="Off" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__off__">Off</SelectItem>
            {dateFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        {compare && (
          <Select value={compare.period || "month"} onValueChange={(v) => setCompare(compare.field, v)}>
            <SelectTrigger data-testid="kpi-compare-period" className="bg-[color:var(--surface)] w-[100px] shrink-0"><SelectValue /></SelectTrigger>
            <SelectContent>{["day", "month", "quarter", "year"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {compare && <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Shows the latest {compare.period} vs the previous one with % change.</p>}
    </div>
  );
}

function QuickCalcRow({ measure, index, update, disabled }) {
  if (disabled) return null;
  const qc = measure?.quickCalc || { kind: "none" };
  const kind = qc.kind || "none";
  const setKind = (k) => {
    if (k === "none") { update(index, "quickCalc", null); return; }
    const next = { kind: k };
    if (k === "moving_avg") next.window = qc.window || 3;
    if (k === "rank") next.direction = qc.direction || "desc";
    update(index, "quickCalc", next);
  };
  return (
    <div className="flex gap-1.5 items-center pl-2 border-l-2 border-[color:var(--glass-border)] ml-1">
      <Sigma className="w-3 h-3 shrink-0 text-[color:var(--ink-3)]" />
      <Select value={kind} onValueChange={setKind}>
        <SelectTrigger data-testid={`quickcalc-${index}`} className="bg-[color:var(--surface)] flex-1 h-7 text-[11px] min-w-0"><SelectValue placeholder="Show value as…" /></SelectTrigger>
        <SelectContent>{QUICK_CALCS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
      </Select>
      {kind === "moving_avg" && (
        <Input data-testid={`quickcalc-window-${index}`} type="number" min={1} max={50} value={qc.window || 3}
          onChange={(e) => update(index, "quickCalc", { ...qc, window: Math.max(1, Math.min(50, Number(e.target.value) || 3)) })}
          className="bg-[color:var(--surface)] w-[64px] h-7 text-[11px]" title="Window size (N)" />
      )}
      {kind === "rank" && (
        <Select value={qc.direction || "desc"} onValueChange={(v) => update(index, "quickCalc", { ...qc, direction: v })}>
          <SelectTrigger data-testid={`quickcalc-rank-dir-${index}`} className="bg-[color:var(--surface)] w-[86px] h-7 text-[11px] shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">High→Low</SelectItem>
            <SelectItem value="asc">Low→High</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

